/**
 * When the page last went to the background, and when it last came back.
 *
 * WHY THIS EXISTS. Masked "Script error." events from the Maps chunk scripts cannot be unmasked: those scripts send no
 * Access-Control-Allow-Origin header (measured 2026-10-28, see trustedScriptCors.ts), so loading them in CORS mode would
 * stop Maps loading. Every technician event in error_groups 6d4edc24b87fc369 follows a "Navigate" tap (a `window.open` to
 * Google Maps) by about a second, which on a phone sends this tab to the background. Recording the visibility at the moment
 * of the throw answers the one question a human can act on: was the page on screen, so did anyone see something break?
 *
 * `null` means "not since install", never zero: a page that was never hidden must not read as "hidden 0ms ago". A page that
 * is already hidden at install (a background tab, a prerender) counts as hidden from that moment.
 *
 * Installs are counted, like surfaceRegistry's mounts: a second install (StrictMode, HMR) shares the one listener, and only
 * the last cleanup removes it. Each cleanup is idempotent. Never throws: it runs inside the error handler.
 */

export interface PageVisibilitySnapshot {
  visibilityState: DocumentVisibilityState | 'unknown';
  msSinceHidden: number | null;
  msSinceVisible: number | null;
}

let installs = 0;
let lastHiddenAt: number | null = null;
let lastVisibleAt: number | null = null;

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') lastHiddenAt = Date.now();
  else lastVisibleAt = Date.now();
}

/** Start the clock. The returned cleanup stops it (once the last install is released) and forgets what it saw. */
export function installPageVisibilityClock(): () => void {
  if (installs === 0) {
    lastHiddenAt = document.visibilityState === 'hidden' ? Date.now() : null;
    lastVisibleAt = null;
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  installs += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    installs -= 1;
    if (installs > 0) return;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    lastHiddenAt = null;
    lastVisibleAt = null;
  };
}

/** Clamped at zero: a wall-clock step on a phone must not produce a negative age. */
function msSince(at: number | null, now: number): number | null {
  return at == null ? null : Math.max(0, now - at);
}

export function getPageVisibilitySnapshot(): PageVisibilitySnapshot {
  try {
    const now = Date.now();
    return {
      visibilityState: document.visibilityState ?? 'unknown',
      msSinceHidden: msSince(lastHiddenAt, now),
      msSinceVisible: msSince(lastVisibleAt, now),
    };
  } catch {
    return { visibilityState: 'unknown', msSinceHidden: null, msSinceVisible: null };
  }
}
